// Single source of truth for employee input (manual + bulk upload).
// Bulk schema mirrors Add Employee (steps 1–5): personal, org, compensation, banking, statutory.
// Employee code is auto-generated — not in upload files.

import { DEFAULT_EMPLOYEE_TYPE, EMPLOYEE_TYPES } from "./employeeTypeConstants";
import { DEFAULT_EMPLOYEE_ROLE, SYSTEM_ROLES } from "./employeeRoleConstants";

export const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
export const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Divorced", "Widowed"];
export const STATUS_OPTIONS = ["active", "inactive", "on_leave", "terminated"];
export const EMPLOYMENT_CATEGORY_OPTIONS = [
  "employee",
  "worker",
  "apprentice",
  "trainee",
  "contract",
  "consultant",
  "intern",
];
export const PAY_FREQUENCY_OPTIONS = ["Monthly"];

const ROLE_VALUES = SYSTEM_ROLES.map((r) => r.value);

/** Keys that belong on the Employee model (not banking / statutory side tables). */
export const EMPLOYEE_CORE_KEYS = new Set([
  "employee_type",
  "first_name",
  "middle_name",
  "last_name",
  "display_name",
  "work_email",
  "email",
  "mobile_phone",
  "phone",
  "gender",
  "date_of_birth",
  "marital_status",
  "fathers_name",
  "role",
  "department_id",
  "designation_id",
  "location_id",
  "business_unit",
  "date_of_joining",
  "status",
  "annual_ctc",
  "pay_frequency",
  "is_active",
  "reporting_manager_id",
  "manager_id",
  "pay_structure_id",
  "date_of_leaving",
  "uan_link_status",
  "employee_code",
]);

export const BANKING_BULK_KEYS = [
  "ifsc_code",
  "bank_name",
  "branch_name",
  "account_holder_name",
  "account_number",
];

export const STATUTORY_BULK_KEYS = [
  "uan_number",
  "pan",
  "aadhaar",
  "pf_number",
  "esic_number",
  "lwf_number",
  "employment_category",
  "statutory_effective_from",
  "pf_member",
  "esi_applicable",
  "pt_applicable",
  "lwf_applicable",
  "bonus_applicable",
];

export const EMPLOYEE_FIELDS = [
  {
    key: "employee_type",
    label: "Employee Type",
    required: true,
    type: "enum",
    enumValues: EMPLOYEE_TYPES,
    defaultValue: DEFAULT_EMPLOYEE_TYPE,
    group: "Basic",
    hint: "Employment category. Default: Permanent",
    example: DEFAULT_EMPLOYEE_TYPE,
    bulk: {
      required: true,
      headerAliases: ["employee_type", "employee type", "employment_type", "emp_type"],
    },
  },
  {
    key: "first_name",
    label: "First Name",
    required: true,
    type: "string",
    group: "Basic",
    example: "Aarav",
    bulk: { required: true, headerAliases: ["first_name", "first name", "firstname", "name_first"] },
  },
  {
    key: "middle_name",
    label: "Middle Name",
    required: false,
    type: "string",
    group: "Basic",
    example: "Kumar",
    bulk: { required: false, headerAliases: ["middle_name", "middle name", "middlename"] },
  },
  {
    key: "last_name",
    label: "Last Name",
    required: false,
    type: "string",
    group: "Basic",
    example: "Sharma",
    bulk: { required: false, headerAliases: ["last_name", "last name", "lastname", "surname"] },
  },
  {
    key: "display_name",
    label: "Display Name",
    required: true,
    type: "string",
    group: "Basic",
    hint: "Shown on payslips if different from legal name",
    example: "Aarav S.",
    bulk: { required: false, headerAliases: ["display_name", "display name"] },
  },
  {
    key: "work_email",
    label: "Work Email",
    required: true,
    type: "email",
    group: "Basic",
    hint: "Must be a valid email address.",
    example: "aarav@company.com",
    bulk: {
      required: true,
      headerAliases: ["work_email", "work email", "email", "email_address"],
    },
  },
  {
    key: "mobile_phone",
    label: "Mobile Phone",
    required: false,
    type: "phone",
    group: "Basic",
    hint: "Include country code if possible. Example: +91 9876543210",
    example: "+91 9876543210",
    bulk: {
      required: false,
      headerAliases: ["mobile_phone", "mobile phone", "phone", "contact"],
    },
  },
  {
    key: "gender",
    label: "Gender",
    required: true,
    type: "enum",
    enumValues: GENDER_OPTIONS,
    group: "Basic",
    example: "Male",
    bulk: { required: true, headerAliases: ["gender", "sex"] },
  },
  {
    key: "date_of_birth",
    label: "Date of Birth",
    required: true,
    type: "date",
    group: "Basic",
    hint: "YYYY-MM-DD. Age must be below 100 years.",
    example: "1996-08-12",
    bulk: { required: false, headerAliases: ["date_of_birth", "dob", "birth_date", "birth date"] },
  },
  {
    key: "marital_status",
    label: "Marital Status",
    required: false,
    type: "enum",
    enumValues: MARITAL_STATUS_OPTIONS,
    group: "Basic",
    example: "Single",
    bulk: { required: false, headerAliases: ["marital_status", "marital status"] },
  },
  {
    key: "fathers_name",
    label: "Father's Name",
    required: false,
    type: "string",
    group: "Basic",
    example: "Ramesh Sharma",
    bulk: {
      required: false,
      headerAliases: ["fathers_name", "father_name", "father's name", "fathers name"],
    },
  },
  {
    key: "role",
    label: "System Access Role",
    required: true,
    type: "enum",
    enumValues: ROLE_VALUES,
    defaultValue: DEFAULT_EMPLOYEE_ROLE,
    group: "Organization",
    hint: "employee, manager, hr, or admin",
    example: DEFAULT_EMPLOYEE_ROLE,
    bulk: {
      required: true,
      headerAliases: ["role", "system_role", "access_role", "user_role"],
    },
  },
  {
    key: "department_id",
    label: "Department",
    required: false,
    type: "fk",
    group: "Organization",
    hint: "Use Department UUID, or provide department_name for auto-mapping.",
    example: "",
    fk: { kind: "departments", idKey: "id", nameKey: "name" },
    bulk: {
      required: false,
      headerAliases: ["department_id", "department id", "dept_id"],
      secondaryNameKey: "department_name",
      secondaryHeaderAliases: ["department_name", "department", "dept", "department name"],
    },
  },
  {
    key: "designation_id",
    label: "Designation",
    required: false,
    type: "fk",
    group: "Organization",
    hint: "Use Designation UUID, or provide designation_name for auto-mapping.",
    example: "",
    fk: { kind: "designations", idKey: "id", nameKey: "name" },
    bulk: {
      required: false,
      headerAliases: ["designation_id", "designation id", "desig_id"],
      secondaryNameKey: "designation_name",
      secondaryHeaderAliases: ["designation_name", "designation", "title", "designation name"],
    },
  },
  {
    key: "location_id",
    label: "Location",
    required: false,
    type: "fk",
    group: "Organization",
    hint: "Use Location UUID, or provide location_name for auto-mapping.",
    example: "",
    fk: { kind: "locations", idKey: "id", nameKey: "name" },
    bulk: {
      required: false,
      headerAliases: ["location_id", "location id", "work_location_id"],
      secondaryNameKey: "location_name",
      secondaryHeaderAliases: ["location_name", "location", "work location", "location name"],
    },
  },
  {
    key: "business_unit",
    label: "Business Unit",
    required: false,
    type: "string",
    group: "Organization",
    hint: "Optional business unit / cost centre label.",
    example: "India Ops",
    bulk: {
      required: false,
      headerAliases: ["business_unit", "business unit", "bu", "cost_centre", "cost_center"],
    },
  },
  {
    key: "date_of_joining",
    label: "Date of Joining",
    required: true,
    type: "date",
    group: "Compensation",
    hint: "YYYY-MM-DD. In Add Employee UI this defaults to the 1st of the current month.",
    example: "2026-04-01",
    bulk: {
      required: true,
      headerAliases: ["date_of_joining", "joining_date", "doj", "date of joining"],
    },
  },
  {
    key: "status",
    label: "Employee Status",
    required: false,
    type: "enum",
    enumValues: STATUS_OPTIONS,
    defaultValue: "active",
    group: "Compensation",
    hint: "active, inactive, on_leave, or terminated. Default: active",
    example: "active",
    bulk: {
      required: false,
      headerAliases: ["status", "employee_status", "employment_status"],
    },
  },
  {
    key: "annual_ctc",
    label: "Annual CTC",
    required: true,
    type: "number",
    group: "Compensation",
    hint: "Annual salary in INR. Must be greater than zero.",
    example: "600000",
    bulk: {
      required: true,
      headerAliases: ["annual_ctc", "ctc", "salary", "annual salary"],
    },
  },
  {
    key: "pay_frequency",
    label: "Pay Frequency",
    required: false,
    type: "enum",
    enumValues: PAY_FREQUENCY_OPTIONS,
    defaultValue: "Monthly",
    group: "Compensation",
    hint: "Fixed to Monthly (same as Add Employee).",
    example: "Monthly",
    bulk: {
      required: false,
      headerAliases: ["pay_frequency", "pay frequency", "salary_frequency"],
    },
  },

  // ── Step 4 — Banking (optional; if any banking cell is filled, IFSC + account number required) ──
  {
    key: "ifsc_code",
    label: "IFSC Code",
    required: false,
    type: "ifsc",
    group: "Banking",
    hint: "11-char IFSC (e.g. HDFC0001234). Bank & branch auto-resolve on import.",
    example: "HDFC0001234",
    bulk: {
      required: false,
      headerAliases: ["ifsc_code", "ifsc", "ifsc code"],
    },
  },
  {
    key: "bank_name",
    label: "Bank Name",
    required: false,
    type: "string",
    group: "Banking",
    hint: "Optional — auto-filled from IFSC when blank.",
    example: "HDFC Bank",
    bulk: {
      required: false,
      headerAliases: ["bank_name", "bank", "bank name"],
    },
  },
  {
    key: "branch_name",
    label: "Branch Name",
    required: false,
    type: "string",
    group: "Banking",
    hint: "Optional — auto-filled from IFSC when blank.",
    example: "Koramangala",
    bulk: {
      required: false,
      headerAliases: ["branch_name", "branch", "bank_branch", "branch name"],
    },
  },
  {
    key: "account_holder_name",
    label: "Account Holder Name",
    required: false,
    type: "string",
    group: "Banking",
    hint: "Defaults to employee name when blank.",
    example: "Aarav Sharma",
    bulk: {
      required: false,
      headerAliases: [
        "account_holder_name",
        "account holder name",
        "account_holder",
        "beneficiary_name",
      ],
    },
  },
  {
    key: "account_number",
    label: "Account Number",
    required: false,
    type: "account_number",
    group: "Banking",
    hint: "9–18 digits. Used as the primary salary account.",
    example: "50100234567890",
    bulk: {
      required: false,
      headerAliases: [
        "account_number",
        "account number",
        "bank_account_number",
        "bank_account",
        "a/c_no",
        "ac_no",
      ],
    },
  },

  // ── Step 5 — Statutory (optional; leave blank if not applicable) ──
  {
    key: "uan_number",
    label: "UAN Number",
    required: false,
    type: "uan",
    group: "Statutory",
    hint: "12-digit EPFO UAN.",
    example: "100123456789",
    bulk: {
      required: false,
      headerAliases: ["uan_number", "uan", "uan number", "epfo_uan"],
    },
  },
  {
    key: "pan",
    label: "PAN Number",
    required: false,
    type: "pan",
    group: "Statutory",
    hint: "Format: AAAAA9999A",
    example: "ABCDE1234F",
    bulk: {
      required: false,
      headerAliases: ["pan", "pan_number", "pan number", "permanent_account_number"],
    },
  },
  {
    key: "aadhaar",
    label: "Aadhaar Number",
    required: false,
    type: "aadhaar",
    group: "Statutory",
    hint: "Optional 12-digit Aadhaar with valid checksum. Leave blank in sample; fill real values before upload.",
    example: "",
    bulk: {
      required: false,
      headerAliases: ["aadhaar", "aadhaar_number", "aadhar", "aadhar_number", "uid"],
    },
  },
  {
    key: "pf_number",
    label: "PF Member ID",
    required: false,
    type: "string",
    group: "Statutory",
    hint: "PF Member ID from EPF establishment.",
    example: "DLCPM12345670000012345",
    bulk: {
      required: false,
      headerAliases: ["pf_number", "pf_member_id", "pf number", "epf_number"],
    },
  },
  {
    key: "esic_number",
    label: "ESIC Insurance Number",
    required: false,
    type: "esic",
    group: "Statutory",
    hint: "10-digit ESIC number when ESI-applicable.",
    example: "1234567890",
    bulk: {
      required: false,
      headerAliases: ["esic_number", "esi_number", "esic", "esi number"],
    },
  },
  {
    key: "lwf_number",
    label: "LWF Number",
    required: false,
    type: "string",
    group: "Statutory",
    hint: "Labour Welfare Fund number where applicable.",
    example: "LWF-KA-10021",
    bulk: {
      required: false,
      headerAliases: ["lwf_number", "lwf", "labour_welfare_number"],
    },
  },
  {
    key: "employment_category",
    label: "Employment Category",
    required: false,
    type: "enum",
    enumValues: EMPLOYMENT_CATEGORY_OPTIONS,
    defaultValue: "employee",
    group: "Statutory",
    hint: "Statutory employment classification. Default: employee",
    example: "employee",
    bulk: {
      required: false,
      headerAliases: ["employment_category", "employment category", "stat_category"],
    },
  },
  {
    key: "statutory_effective_from",
    label: "Statutory Effective From",
    required: false,
    type: "date",
    group: "Statutory",
    hint: "YYYY-MM-DD. Defaults to date_of_joining when statutory IDs are provided.",
    example: "2026-04-01",
    bulk: {
      required: false,
      headerAliases: [
        "statutory_effective_from",
        "effective_from",
        "stat_effective_from",
        "statutory effective from",
      ],
    },
  },
  {
    key: "pf_member",
    label: "PF Applicable",
    required: false,
    type: "boolean",
    defaultValue: false,
    group: "Statutory",
    hint: "true/false. Default false. When true, UAN and PF Member ID are required.",
    example: "true",
    bulk: {
      required: false,
      headerAliases: ["pf_member", "pf_applicable", "pf applicable"],
    },
  },
  {
    key: "esi_applicable",
    label: "ESI Applicable",
    required: false,
    type: "boolean",
    defaultValue: false,
    group: "Statutory",
    hint: "true/false. Default false. When true, ESIC Insurance Number is required.",
    example: "true",
    bulk: {
      required: false,
      headerAliases: ["esi_applicable", "esi applicable", "esic_applicable"],
    },
  },
  {
    key: "pt_applicable",
    label: "PT Applicable",
    required: false,
    type: "boolean",
    defaultValue: true,
    group: "Statutory",
    hint: "true/false. Default true when statutory row is created.",
    example: "true",
    bulk: {
      required: false,
      headerAliases: ["pt_applicable", "pt applicable", "professional_tax"],
    },
  },
  {
    key: "lwf_applicable",
    label: "LWF Applicable",
    required: false,
    type: "boolean",
    defaultValue: false,
    group: "Statutory",
    hint: "true/false. Default false.",
    example: "false",
    bulk: {
      required: false,
      headerAliases: ["lwf_applicable", "lwf applicable"],
    },
  },
  {
    key: "bonus_applicable",
    label: "Bonus Applicable",
    required: false,
    type: "boolean",
    defaultValue: false,
    group: "Statutory",
    hint: "true/false. Default false.",
    example: "false",
    bulk: {
      required: false,
      headerAliases: ["bonus_applicable", "bonus applicable"],
    },
  },
];

export const EMPLOYEE_GROUPS = [
  { key: "Basic", title: "Step 1 — Personal details (same as Add Employee)" },
  { key: "Organization", title: "Step 2 — Organization" },
  { key: "Compensation", title: "Step 3 — Compensation" },
  { key: "Banking", title: "Step 4 — Banking (optional — leave blank to skip)" },
  { key: "Statutory", title: "Step 5 — Statutory (optional — leave blank to skip)" },
];

export const BULK_UPLOAD_NOTES = [
  "Employee code is auto-generated on save — do not include an employee_code column.",
  "Template mirrors Add Employee steps 1–5: personal, organization, compensation, banking, and statutory.",
  "Banking and statutory columns are optional — leave them blank to skip (same as Skip on Add Employee). If you fill any banking cell, provide ifsc_code and account_number.",
  "Replace sample IFSC / account / PAN / UAN with real employee values before upload. Bank name and branch resolve from IFSC.",
  "Aadhaar is optional; when provided it must be a valid 12-digit number (checksum validated on import).",
  "Use department_name / designation_name / location_name when you do not have UUIDs.",
  "Date format: YYYY-MM-DD. Date of birth age must be below 100 years. Prefer date_of_joining as the 1st of the joining month when applicable.",
  "Boolean columns accept true/false, yes/no, or 1/0.",
];

export function normaliseHeader(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

export function getBulkTargetFields() {
  return EMPLOYEE_FIELDS.filter((f) => f.bulk);
}

export function getCanonicalBulkHeaders({ includeSecondaryNameColumns = true } = {}) {
  const headers = [];
  for (const f of getBulkTargetFields()) {
    headers.push(f.key);
    if (includeSecondaryNameColumns && f.bulk?.secondaryNameKey) {
      headers.push(f.bulk.secondaryNameKey);
    }
  }
  return headers;
}

export function getSampleRows() {
  return [
    {
      employee_type: "Permanent",
      first_name: "Aarav",
      middle_name: "",
      last_name: "Sharma",
      display_name: "",
      work_email: "aarav.sharma@company.com",
      mobile_phone: "+91 9876543210",
      gender: "Male",
      date_of_birth: "1996-08-12",
      marital_status: "Single",
      fathers_name: "Ramesh Sharma",
      role: "employee",
      department_id: "",
      department_name: "Engineering",
      designation_id: "",
      designation_name: "Software Engineer",
      location_id: "",
      location_name: "Bengaluru",
      business_unit: "India Ops",
      date_of_joining: "2026-04-01",
      status: "active",
      annual_ctc: "850000",
      pay_frequency: "Monthly",
      ifsc_code: "HDFC0001234",
      bank_name: "HDFC Bank",
      branch_name: "Koramangala",
      account_holder_name: "Aarav Sharma",
      account_number: "50100234567890",
      uan_number: "100123456789",
      pan: "ABCDE1234F",
      aadhaar: "",
      pf_number: "DLCPM12345670000012345",
      esic_number: "1234567890",
      lwf_number: "",
      employment_category: "employee",
      statutory_effective_from: "2026-04-01",
      pf_member: "true",
      esi_applicable: "true",
      pt_applicable: "true",
      lwf_applicable: "false",
      bonus_applicable: "false",
    },
    {
      employee_type: "Contract",
      first_name: "Diya",
      middle_name: "",
      last_name: "Patel",
      display_name: "Diya P.",
      work_email: "diya.patel@company.com",
      mobile_phone: "+91 9998887776",
      gender: "Female",
      date_of_birth: "1994-02-03",
      marital_status: "Married",
      fathers_name: "Suresh Patel",
      role: "employee",
      department_id: "",
      department_name: "HR",
      designation_id: "",
      designation_name: "HR Executive",
      location_id: "",
      location_name: "Mumbai",
      business_unit: "",
      date_of_joining: "2026-05-15",
      status: "active",
      annual_ctc: "520000",
      pay_frequency: "Monthly",
      ifsc_code: "SBIN0000456",
      bank_name: "State Bank of India",
      branch_name: "Andheri West",
      account_holder_name: "Diya Patel",
      account_number: "30123456789",
      uan_number: "100987654321",
      pan: "FGHIJ5678K",
      aadhaar: "",
      pf_number: "",
      esic_number: "",
      lwf_number: "",
      employment_category: "contract",
      statutory_effective_from: "2026-05-15",
      pf_member: "true",
      esi_applicable: "false",
      pt_applicable: "true",
      lwf_applicable: "false",
      bonus_applicable: "false",
    },
    {
      employee_type: "Intern",
      first_name: "Ibrahim",
      middle_name: "Ali",
      last_name: "Khan",
      display_name: "",
      work_email: "ibrahim.khan@company.com",
      mobile_phone: "+91 7000012345",
      gender: "Male",
      date_of_birth: "2002-11-25",
      marital_status: "Single",
      fathers_name: "Imran Khan",
      role: "employee",
      department_id: "",
      department_name: "Finance",
      designation_id: "",
      designation_name: "Accounts Intern",
      location_id: "",
      location_name: "Delhi",
      business_unit: "",
      date_of_joining: "2026-06-01",
      status: "active",
      annual_ctc: "240000",
      pay_frequency: "Monthly",
      ifsc_code: "ICIC0000789",
      bank_name: "ICICI Bank",
      branch_name: "Connaught Place",
      account_holder_name: "Ibrahim Ali Khan",
      account_number: "009876543210",
      uan_number: "",
      pan: "KLMNO9012P",
      aadhaar: "",
      pf_number: "",
      esic_number: "",
      lwf_number: "",
      employment_category: "intern",
      statutory_effective_from: "2026-06-01",
      pf_member: "false",
      esi_applicable: "false",
      pt_applicable: "true",
      lwf_applicable: "false",
      bonus_applicable: "false",
    },
  ];
}

export function getBulkFieldByKey(key) {
  return EMPLOYEE_FIELDS.find((f) => f.key === key);
}

export function rowHasBankingData(record) {
  return BANKING_BULK_KEYS.some((k) => {
    const v = record?.[k];
    return v !== null && v !== undefined && String(v).trim() !== "";
  });
}

export function rowHasStatutoryData(record) {
  const idKeys = ["uan_number", "pan", "aadhaar", "pf_number", "esic_number", "lwf_number"];
  return idKeys.some((k) => {
    const v = record?.[k];
    return v !== null && v !== undefined && String(v).trim() !== "";
  });
}
